import {action, runInAction} from 'mobx';
import last from 'lodash/last';
import {ISmsService} from '../../services/SmsService';
import {EVENT_TYPES, ISmsData} from '../../services/ts-sms/ts-sms.settings';
import {SendSmsStatus} from './_contracts/SendSmsStatus';
import {IInviteStateManager} from './invite.persister';
import {AccountSettings} from '../../models/AccountSettings';
import {getRootStore} from '../../app.bootstrap';
import {IJsApiService, MessagesStatus, MessageType} from '../../services/JsApiService';
import {ITranslate} from '../../services/LocalizationService';
import {ChangeNumberController} from './invite-wizard/components/change-number/controller';
import {ChangeNumberOrigins} from './_contracts/InviteContracts';
import {SmsStatusParams} from '../../services/Session/SessionContracts';
import {extractSmsSendingError, shouldOfferNumberChange} from './invite.sms.errors';

export class InviteSmsFlow {
    private _stateManager: IInviteStateManager;

    private _jsApiService: IJsApiService;

    private _smsService: ISmsService;

    private _accountSettings: AccountSettings;

    private _translate: ITranslate;

    private isCanceled: boolean = false;

    private _changeNumberController?: ChangeNumberController;

    constructor(
        stateManager: IInviteStateManager,
        smsService: ISmsService,
        settings: AccountSettings,
        translate: ITranslate,
        changeNumberController: ChangeNumberController | undefined = undefined
    ) {
        if (!stateManager.isReady) {
            throw new Error('StateManager should be ready before passing it to this class.');
        }

        this._stateManager = stateManager;
        this._smsService = smsService;
        this._accountSettings = settings;
        this._translate = translate;
        this._jsApiService = getRootStore().jsApiService;
        this._changeNumberController = changeNumberController;
    }

    async sendSms(): Promise<void> {
        const smsData = this.getSendSmsParams();
        const addressType = this._stateManager.inviteState.inviteMethodInfo.addressType;
        const msgParams = {
            type: MessageType.smsSent,
            techseeSessionId: this._stateManager.inviteState.sessionInfo.sessionId,
            customerNumber: this._stateManager.inviteState.inviteMethodInfo.address,
            customerId: this._stateManager.inviteState.inviteMethodInfo.customerRef,
            sessionType: this._stateManager.inviteState.sessionInfo.meetingMode
        };

        return new Promise((resolve, reject) => {
            this._smsService
                .sendSms(smsData, this.handleSmsProgressEvent.bind(this), undefined, undefined, addressType)
                .then((sendSmsResult) => {
                    if (this.isCanceled) {
                        return;
                    }

                    if (sendSmsResult && sendSmsResult.success !== false) {
                        const msg = {...msgParams, status: MessagesStatus.Success};

                        if (sendSmsResult.isWhatsApp) {
                            this._jsApiService.SMSSent(msg);
                        }
                        resolve();
                    } else {
                        const msg = {
                            ...msgParams,
                            error: extractSmsSendingError(last(this.sendSmsProgress)?.messageDetails, this._translate),
                            status: MessagesStatus.Failed
                        };

                        if (sendSmsResult.isWhatsApp) {
                            this._jsApiService.SMSSent(msg);
                        }
                        reject(last(this.sendSmsProgress)?.messageDetails);
                    }
                })
                .catch(() => {
                    if (!this.isCanceled) {
                        const msg = {
                            ...msgParams,
                            error: extractSmsSendingError(last(this.sendSmsProgress)?.messageDetails, this._translate),
                            status: MessagesStatus.Failed
                        };

                        this._jsApiService.SMSSent(msg);

                        reject(last(this.sendSmsProgress)?.messageDetails);
                    }
                });
        });
    }

    smsStatusChanged(data: SmsStatusParams): void {
        this._smsService.smsStatusChanged(data, this.handleSmsProgressEvent.bind(this));
    }

    restoreSms(data: any) {
        this._smsService?.restoreSms(data, this.handleSmsProgressEvent.bind(this));
    }

    cancel() {
        this.isCanceled = true;
        this._smsService.abortSendSms();
    }

    @action
    private handleSmsProgressEvent(message: any): void {
        const msgParams = {
            type: MessageType.smsSent,
            techseeSessionId: this._stateManager.inviteState.sessionInfo.sessionId,
            customerNumber: this._stateManager.inviteState.inviteMethodInfo.address,
            customerId: this._stateManager.inviteState.inviteMethodInfo.customerRef,
            sessionType: this._stateManager.inviteState.sessionInfo.meetingMode
        };

        if (this.isCanceled || !message || !message.type) {
            return;
        }

        if (
            message.type === EVENT_TYPES.ERROR &&
            this.sendSmsProgress.length > 0 &&
            this.sendSmsProgress[this.sendSmsProgress.length - 1].name === EVENT_TYPES.ERROR
        ) {
            this.sendSmsProgress.splice(
                this.sendSmsProgress.length - 1,
                1,
                new SendSmsStatus(message.type, true, message.data)
            );

            return;
        }

        if (
            message.type === EVENT_TYPES.CLIENT_RECEIVED &&
            this.sendSmsProgress.length > 0 &&
            (this.sendSmsProgress[this.sendSmsProgress.length - 1].name === EVENT_TYPES.WAITING_FOR_CLIENT ||
                this.sendSmsProgress[this.sendSmsProgress.length - 1].name === EVENT_TYPES.SENDING_WHATSAPP)
        ) {
            this._smsService.abortSendSms();
            return;
        }

        if (message.type !== EVENT_TYPES.UNKNOWN) {
            runInAction(() => {
                if (this.sendSmsProgress.length > 0) {
                    this.sendSmsProgress[this.sendSmsProgress.length - 1].isActive = false;
                }
                if (message.type === EVENT_TYPES.RESENDING_SMS) {
                    this.sendSmsProgress.push(new SendSmsStatus(EVENT_TYPES.ERROR, false, {}));
                }
                if (message.type === EVENT_TYPES.ERROR) {
                    const msg = {
                        ...msgParams,
                        error: extractSmsSendingError(message.data, this._translate),
                        status: MessagesStatus.Failed
                    };
                    this._jsApiService.SMSSent(msg);

                    if (shouldOfferNumberChange(message.data)) {
                        this._changeNumberController?.showChangeNumberDialog(ChangeNumberOrigins.SMSFAILED);
                    }

                    this._smsService.abortSendSms();
                }

                this.sendSmsProgress.push(new SendSmsStatus(message.type, true, message.data));

                if (message.type === EVENT_TYPES.SMS_DELIVERED) {
                    this.sendSmsProgress[this.sendSmsProgress.length - 1].isActive = false;
                    this.sendSmsProgress.push(new SendSmsStatus(EVENT_TYPES.WAITING_FOR_CLIENT, true));
                }

                if (message.type === EVENT_TYPES.SMS_DELIVERED || message.type === EVENT_TYPES.CLIENT_RECEIVED) {
                    const msg = {...msgParams, status: MessagesStatus.Success};
                    this._jsApiService.SMSSent(msg);

                    this._smsService.abortSendSms();
                }
            });
        }
    }

    private getSendSmsParams(): ISmsData {
        const countryCode = this._stateManager.inviteState.inviteMethodInfo.countryCode
            ? this._stateManager.inviteState.inviteMethodInfo.countryCode.replace(/[^0-9]/g, '')
            : '';

        const smsData: ISmsData = {
            phone: this._stateManager.inviteState.inviteMethodInfo.phoneNumber || '',
            roomId: this._stateManager.inviteState.sessionInfo.sessionRoomId,
            link: this._stateManager.inviteState.sessionInfo.sessionLink,
            chromeDetection: this._accountSettings.sessionSettings.chromeDetection,
            cameraModeOnly: this._accountSettings.sessionSettings.cameraModeOnly,
            offline: this._stateManager.inviteState.sessionInfo.isOffline,
            showSmsPreview: true,
            countryCode: countryCode,
            startWithAgentType: this._stateManager.inviteState.sessionInfo.meetingMode,
            smsUseNumberLookup: false,
            audio: this._stateManager.inviteState.inviteMethodInfo.enableAudio
        };

        return smsData;
    }

    private get sendSmsProgress(): SendSmsStatus[] {
        return this._stateManager.inviteState.smsFlowProgress;
    }
}
